import dayjs from 'dayjs';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const FORMATS = [
  'M/D/YYYY H:mm',
  'MM/DD/YY HH:mm', // Format: %m/%d/%y %H:%M
  'MM/DD/YYYY HH:mm', // Format: %m/%d/%Y %H:%M
  'YYYY/MM/DD hh:mm:ss A', // Format: %Y/%m/%d %I:%M:%S %p
];

function lastMonth(zoneName) {
  const lastMonth = dayjs().tz(zoneName).subtract(1, 'month');
  return { start: lastMonth.startOf('month'), end: lastMonth.endOf('month') };
}

function thisMonth(zoneName) {
  const thisMonth = dayjs().tz(zoneName);
  return { start: thisMonth.startOf('month'), end: thisMonth.endOf('month') };
}

function lastWeek(zoneName) {
  const lastWeek = dayjs().tz(zoneName).subtract(1, 'week');
  return { start: lastWeek.startOf('week'), end: lastWeek.endOf('week') };
}

function thisWeek(zoneName) {
  const thisWeek = dayjs().tz(zoneName);
  return { start: thisWeek.startOf('week'), end: thisWeek.endOf('week') };
}

function yesterday(zoneName) {
  const yesterday = dayjs().tz(zoneName).subtract(1, 'day');
  return { start: yesterday.startOf('day'), end: yesterday.endOf('day') };
}

function today(zoneName) {
  const today = dayjs().tz(zoneName);
  return { start: today.startOf('day'), end: today.endOf('day') };
}

function current(zoneName) {
  const today = dayjs().tz(zoneName);
  const start = today.subtract(1, 'day').startOf('day');
  const end = today.add(1, 'day').endOf('day');
  return { start, end };
}

function getLocalTimzone() {
  const now = dayjs();
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const name = now.tz(zone).format('z');
  const offset = now.tz(zone).utcOffset() / 60;

  return { name, zone, offset };
}

/**
 * Function to detect format, convert to Unix time, and apply timezone offset
 * @param {string} timestamp - The date string to parse
 * @param {number} timezoneOffset - The timezone offset in hours (e.g., -5 for EST)
 * @returns {number | null} - Unix timestamp (in seconds) or null if no format matches
 */
function convertStringToUnix(timestamp, timezoneOffset = 0) {
  let parsedDate = null;

  if (typeof timestamp !== 'string' || timestamp === '') {
    return null;
  }

  for (const format of FORMATS) {
    const date = dayjs(timestamp, format, true); // `true` ensures strict parsing
    if (date.isValid()) {
      parsedDate = date;
      break;
    }
  }

  if (parsedDate) {
    // Adjust the timezone offset (if provided) and convert to Unix timestamp
    let adjustedDate = parsedDate.add(timezoneOffset, 'hour');
    adjustedDate = adjustedDate
      .minute(adjustedDate.minute() - (adjustedDate.minute() % 15))
      .second(0)
      .millisecond(0);
    return adjustedDate.unix(); // Return Unix time (in seconds)
  } else {
    throw new Error(`Invalid format : ${timestamp}`);
  }
}

/**
 * Convert Unix timestamp to a timezone-aware dayjs object
 * @param {number} timestampInSeconds - Unix timestamp in seconds
 * @param {string} tz - Timezone string (e.g., 'America/New_York', 'Europe/London')
 * @returns {dayjs.Dayjs} - A dayjs object aware of the specified timezone
 */
function convertToTimezoneAwareDayjs(timestampInSeconds, tz) {
  return dayjs.unix(timestampInSeconds).tz(tz);
}

const generateTicks = (range) => {
  if (!range.start || !range.end) return [];

  if (range.end.unix() - range.start.unix() < 90000) {
    // return 6am, 12pm and 6 pm
    return [
      range.start.hour(6).minute(0).unix(),
      range.start.hour(12).minute(0).unix(),
      range.start.hour(18).minute(0).unix(),
    ];
  } else {
    // return 12pm for each day in the range
    let ticks = [];
    let currentDate = range.start.hour(12).minute(0);
    while (currentDate <= range.end) {
      ticks.push(currentDate);
      currentDate = currentDate.add(1, 'day');
    }
    return ticks.map((tick) => tick.unix());
  }
};

const getLocalTimezone = () => {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const date = new Date().toLocaleDateString('en-US', {
    timeZoneName: 'short',
  });
  const parts = date.split(' ');
  const name = parts[parts.length - 1];

  const dateTimezoneOffset = new Date().getTimezoneOffset();
  const hours = Math.floor(Math.abs(dateTimezoneOffset) / 60);
  const minutes = Math.abs(dateTimezoneOffset) % 60;
  let offset = hours + minutes / 60;

  if (dateTimezoneOffset > 0) {
    offset = offset * -1;
  }
  return { zone, name, offset };
};

const formatTimestamp = (unixTime, timezone, format = 'MMM Do h:mm A') => {
  if (timezone.zone) {
    return dayjs.unix(unixTime).tz(timezone.zone).format(format);
  }
  return dayjs.unix(unixTime).format(format);
};

export {
  current,
  today,
  yesterday,
  lastWeek,
  thisMonth,
  lastMonth,
  thisWeek,
  getLocalTimzone,
  convertStringToUnix,
  convertToTimezoneAwareDayjs,
  generateTicks,
  getLocalTimezone,
  formatTimestamp,
};
